import type { FC } from 'react';
import { useLocation } from 'react-router';
import type { LoadedAppShellData } from 'src/components/layout/app-shell/use-app-shell-data';
import { useNavigationItems } from 'src/components/layout/app-shell/use-navigation-items';
import type { ActiveSection } from 'src/components/layout/header/types';
import { ExternalLink, Link } from 'src/components/navigation/link';
import { useOptionalAuthenticatedMonolithUser } from 'src/hooks/use-monolith-user';

const isStorageOffersActive = (pathname: string, hash: string) =>
  pathname.startsWith('/storage/offer') ||
  (pathname.startsWith('/platform') && hash.startsWith('#/storage/offers'));

export const isStorageActive = (pathname: string, hash: string) =>
  pathname.startsWith('/storage') ||
  (pathname.startsWith('/platform') && hash.startsWith('#/storage'));

type SecondaryNavStorageProps = {
  activeSection: ActiveSection;
  appShellData: LoadedAppShellData;
};

export const SecondaryNavStorage: FC<SecondaryNavStorageProps> = ({
  activeSection,
  appShellData,
}) => {
  const { hash, pathname } = useLocation();
  const monolithUser = useOptionalAuthenticatedMonolithUser();

  const navigationItems = useNavigationItems(appShellData);

  if (!activeSection.storage) return null;

  const offerCardsEnabled =
    !!appShellData.storageServiceRoot?.response.data._links.getSsoOfferCards;

  return (
    <div
      className={`
        flex flex-col flex-wrap items-stretch gap-x-xl gap-y-md
        md:flex-row md:items-center
      `}
      data-testid="secondary-nav"
    >
      {/* Offers entry - points to cards when the getSsoOfferCards link is present,
          otherwise falls back to the legacy monolith offers page */}
      <Link
        mode="secondary"
        to={offerCardsEnabled ? '/storage/offer-cards' : '/storage/offers'}
        isActive={isStorageOffersActive(pathname, hash)}
      >
        Offers
      </Link>

      {/* FCFS Booking - shown only for shippers */}
      {monolithUser?.isShipper && (
        <ExternalLink
          mode="secondary"
          href={`${PRISMA_CONFIG.angularUrl}/#/storage/booking`}
        >
          FCFS Booking
        </ExternalLink>
      )}

      {navigationItems.storage.locations && (
        <Link mode="secondary" to="/storage/locations">
          Locations
        </Link>
      )}
    </div>
  );
};
